import { GUID } from "@faro-lotv/foundation";
import { clearStore } from "@faro-lotv/project-source";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clearUserEdits } from "../data-preparation-actions";

/**
 * A map from an entity ID to its visibility value.
 * `undefined` is to be treated as `true`, i.e. entities are visible by default.
 */
export type VisibilityMap = Record<GUID, boolean | undefined>;

type DataPreparationUiState = {
  /**
   * The IDs of the entities currently selected by the user,
   * e.g. via the scan tree or directly in the 3D view.
   */
  selectedEntityIds: GUID[];

  /** The hovered entity id in the scan-tree */
  hoveredEntityId?: GUID;

  /** The id of hovered connection and its corresponding scans in the registration scene */
  hoveredConnectionId?: GUID;

  /** The id of selected connection and its corresponding scans in the registration scene */
  selectedConnectionId?: GUID;

  /**
   * A map from an entity ID to its visibility value.
   * `undefined` is to be treated as `true`, i.e. entities are visible by default.
   */
  visibilityMap: VisibilityMap;

  /**
   * The ID of the entity whose context menu should be opened.
   * Set to null once opened.
   */
  entityIdToOpenContextMenu: GUID | null;

  /** Whether a hierarchy change (drag & drop in the scan tree) is currently being saved to the cloud. */
  isSavingHierarchyChanges: boolean;

  /** Whether the most recent hierarchy change failed to save to the cloud. */
  didHierarchySaveFail: boolean;
};

export const DATA_PREPARATION_UI_INITIAL_STATE: Readonly<DataPreparationUiState> =
  Object.freeze({
    selectedEntityIds: [],
    visibilityMap: {},
    entityIdToOpenContextMenu: null,
    isSavingHierarchyChanges: false,
    didHierarchySaveFail: false,
  });

const dataPreparationUiSlice = createSlice({
  initialState: DATA_PREPARATION_UI_INITIAL_STATE,
  name: "data-preparation-ui",

  reducers: {
    /**
     * Clears the previous selection and replaces it with the new one.
     *
     * @param state The current application state
     * @param action The IDs of the entities to select.
     */
    setSelectedEntityIds(state, action: PayloadAction<GUID[]>) {
      state.selectedEntityIds = action.payload;
    },

    /**
     * Remove the entity from the selection.
     * If the entity is not selected, nothing happens.
     *
     * @param state The current application state
     * @param action The ID of the entity to deselect.
     */
    deselectEntity(state, action: PayloadAction<GUID>) {
      state.selectedEntityIds = state.selectedEntityIds.filter(
        (id) => id !== action.payload,
      );
    },

    /**
     * Select/deselect the entity.
     *
     * - If _only_ this entity is selected, deselect it.
     * - Otherwise, select only this entity.
     *
     * @param state The current application state
     * @param action The ID of the entity to select.
     */
    toggleSingleSelection(state, action: PayloadAction<GUID>) {
      if (
        state.selectedEntityIds.length === 1 &&
        state.selectedEntityIds[0] === action.payload
      ) {
        state.selectedEntityIds = [];
      } else {
        state.selectedEntityIds = [action.payload];
      }
    },

    /**
     * Add/remove the entity from the selection.
     *
     * - If the entity is not selected yet, add it to the selection.
     * - If the entity is already selected, remove it from the selection.
     *
     * @param state The current application state
     * @param action The ID of the entity to select.
     */
    toggleMultiSelection(state, action: PayloadAction<GUID>) {
      const index = state.selectedEntityIds.indexOf(action.payload);

      if (index === -1) {
        // Not included in the selection yet, add it
        state.selectedEntityIds.push(action.payload);
      } else {
        // Remove the entity from the selection
        state.selectedEntityIds.splice(index, 1);
      }
    },

    /**
     * Sets the currently hovered entity’s ID.
     *
     * @param state The current application state
     * @param action The entity id that is currently hovered
     */
    setHoveredEntityId(state, action: PayloadAction<GUID | undefined>) {
      state.hoveredEntityId = action.payload;
    },

    /**
     * Clears the currently hovered entity’s ID.
     *
     * @param state The current application state
     * @param action The entity id that should be unset if it is currently hovered
     *  If no id is passed, the hovered entity id is always unset.
     */
    unsetHoveredEntityId(state, action: PayloadAction<GUID | undefined>) {
      if (!action.payload || state.hoveredEntityId === action.payload) {
        state.hoveredEntityId = undefined;
      }
    },

    /**
     * Sets the currently hovered connection ID.
     *
     * @param state The current application state
     * @param action The entity id of the hovered connection
     */
    setHoveredConnectionId(state, action: PayloadAction<GUID | undefined>) {
      state.hoveredConnectionId = action.payload;
    },

    /**
     * Clears the currently hovered connection ID.
     *
     * @param state The current application state
     * @param action The hovered connection id
     */
    unsetHoveredConnectionId(state, action: PayloadAction<GUID | undefined>) {
      if (state.hoveredConnectionId === action.payload) {
        state.hoveredConnectionId = undefined;
      }
    },

    /**
     * Sets the currently selected connection id.
     *
     * @param state The current application state
     * @param action The entity id of the selected connection
     */
    setSelectedConnectionId(state, action: PayloadAction<GUID | undefined>) {
      state.selectedConnectionId = action.payload;
    },

    /**
     * Clears the currently selected connection id.
     *
     * @param state The current application state
     * @param action The selected connection id
     */
    unsetSelectedConnectionId(state, action: PayloadAction<GUID | undefined>) {
      if (state.selectedConnectionId === action.payload) {
        state.selectedConnectionId = undefined;
      }
    },

    /**
     *  Sets the visibility for entities, default is true. The map contains the ids whose visibility is false
     *
     * @param state The current application state
     * @param action object containing set of entity ids with a specific visibility status
     */
    setVisibilityForEntities(
      state,
      action: PayloadAction<{ entityIds: GUID[]; isVisible: boolean }>,
    ) {
      const { entityIds, isVisible } = action.payload;
      if (isVisible) {
        // Default is true
        entityIds.forEach((id) => delete state.visibilityMap[id]);
      } else {
        entityIds.forEach((id) => (state.visibilityMap[id] = false));
      }
    },

    /**
     * Sets the ID of the entity whose context menu should be opened.
     *
     * @param state The current application state
     * @param action The ID of the entity to open the context menu for
     */
    setEntityIdToOpenContextMenu(state, action: PayloadAction<GUID | null>) {
      state.entityIdToOpenContextMenu = action.payload;
    },

    /**
     * Sets whether a hierarchy change is currently being saved to the cloud.
     *
     * @param state The current application state
     * @param action Whether the hierarchy changes are being saved
     */
    setIsSavingHierarchyChanges(state, action: PayloadAction<boolean>) {
      state.isSavingHierarchyChanges = action.payload;
    },

    /**
     * Sets whether the most recent hierarchy change failed to save to the cloud.
     *
     * @param state The current application state
     * @param action Whether the hierarchy save failed
     */
    setDidHierarchySaveFail(state, action: PayloadAction<boolean>) {
      state.didHierarchySaveFail = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearStore, () => DATA_PREPARATION_UI_INITIAL_STATE);

    builder.addCase(clearUserEdits, (state) => {
      state.selectedEntityIds = [];
    });
  },
});

export const {
  setSelectedEntityIds,
  deselectEntity,
  toggleSingleSelection,
  toggleMultiSelection,
  setHoveredEntityId,
  unsetHoveredEntityId,
  setHoveredConnectionId,
  unsetHoveredConnectionId,
  setSelectedConnectionId,
  unsetSelectedConnectionId,
  setVisibilityForEntities,
  setEntityIdToOpenContextMenu,
  setIsSavingHierarchyChanges,
  setDidHierarchySaveFail,
} = dataPreparationUiSlice.actions;

export const dataPreparationUiReducer = dataPreparationUiSlice.reducer;
